import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getGetMessageQueryOptions } from "@/api/orval/user/user";
import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import TopNav from "molecules/navbars/TopNav";
import { extractMessageLinks } from "modules/messages/links/extractMessageLinks";
import { isRelativeAppPath } from "modules/messages/links/classifyLink";
import { useOpenLink } from "modules/messages/links/useOpenLink";
import { markMessageRead } from "modules/messages/readMessages";
import { useUser } from "modules/user";
import { LinkUnavailable } from "pages/LinkUnavailable";
import { isEntityMissingError } from "pages/linkUnavailable.helpers";

import { AccountDestinationSkeleton } from "../AccountDestinationSkeleton";

const MESSAGES_PATH = "/account/messages";

const linkLabel = (link: string): string => link.replace(/^(https?:\/\/)?(www\.)?/, "");

export const MessageDetailPage = () => {
  const { messageId } = useParams();

  if (!messageId) throw new Error("MessageDetailPage rendered without a messageId route param.");

  return <MessageDetail messageId={messageId} />;
};

const MessageDetail = ({ messageId }: { messageId: string }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useUser();
  const { openLink } = useOpenLink();

  const { data, error, isError } = useQuery({
    ...getGetMessageQueryOptions(messageId, { query: { retry: false } }),
  });

  useEffect(() => {
    if (data && user) markMessageRead(user.id, data.id);
  }, [data, user]);

  if (isError) {
    if (isEntityMissingError(error)) return <LinkUnavailable />;
    throw error;
  }

  if (!data) return <AccountDestinationSkeleton title={t("messages.detailHeader")} />;

  const links = extractMessageLinks(data.message);

  return (
    <div className="relative flex min-h-screen flex-col bg-one">
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => navigate(MESSAGES_PATH)}
        showHeaderItems={false}
      >
        <div className="flex max-h-[32px] items-center">
          <div className="flex w-full items-center justify-center text-sub2 uppercase text-primary">
            {t("messages.detailHeader")}
          </div>
        </div>
      </TopNav>

      <article className="mx-4 mt-6 flex flex-col gap-3 rounded-xl bg-white p-5 shadow-sm desk:mx-0">
        <p className="whitespace-pre-wrap text-body1 text-primary">{data.message}</p>
        <p className="text-sub2 text-secondary">{dayjs(data.sentOn).format("lll")}</p>
      </article>

      {links.length > 0 && (
        <div className="flex flex-col gap-3 px-4 pb-8 pt-4 desk:px-0">
          {links.map((link) => (
            <Button
              key={link}
              fullWidth
              style={isRelativeAppPath(link) ? "primary" : "secondary"}
              onClick={() => openLink(link, { returnTo: `${MESSAGES_PATH}/${data.id}` })}
            >
              {linkLabel(link)}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
